import React from 'react';
import { SavedLocation } from '@hero/schema';
import { LocationItem } from './LocationItem';

export interface SavedLocationsTabsProps {
  activeTab: 'myPlaces' | 'client';
  onTabChange: (tab: 'myPlaces' | 'client') => void;
  myPlaces: SavedLocation[];
  clientPlaces: SavedLocation[];
  isLoadingMyPlaces: boolean;
  isLoadingClientPlaces: boolean;
  hasNextMyPlaces: boolean;
  hasNextClientPlaces: boolean;
  isFetchingNextMyPlaces: boolean;
  isFetchingNextClientPlaces: boolean;
  onLoadMoreMyPlaces: () => void;
  onLoadMoreClientPlaces: () => void;
  onSelectLocation: (location: SavedLocation) => void;
  isRTL: boolean;
  t: (key: string, defaultValue?: string) => string;
}

export function SavedLocationsTabs({
  activeTab,
  onTabChange,
  myPlaces,
  clientPlaces,
  isLoadingMyPlaces,
  isLoadingClientPlaces,
  hasNextMyPlaces,
  hasNextClientPlaces,
  isFetchingNextMyPlaces,
  isFetchingNextClientPlaces,
  onLoadMoreMyPlaces,
  onLoadMoreClientPlaces,
  onSelectLocation,
  isRTL,
  t,
}: SavedLocationsTabsProps) {
  return (
    <>
      <div className="p-2 flex-shrink-0">
        <div className="text-xs font-semibold text-gray-500 uppercase px-2 py-1 mb-2">
          {t('location.savedLocations', 'Saved Locations')}
        </div>
        <div className="flex border-b border-gray-200 mb-2">
          <button
            type="button"
            onClick={() => onTabChange('myPlaces')}
            className={`flex-1 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'myPlaces'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {t('location.myPlaces', 'My Places')}
          </button>
          <button
            type="button"
            onClick={() => onTabChange('client')}
            className={`flex-1 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'client'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {t('location.clientPlaces', 'Client Places')}
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {activeTab === 'myPlaces' && (
          <div className="pb-2">
            {isLoadingMyPlaces ? (
              <div className="px-3 py-4 text-center text-sm text-gray-500">
                {t('location.loading', 'Loading...')}
              </div>
            ) : myPlaces.length > 0 ? (
              <>
                {myPlaces.map(savedLocation => (
                  <LocationItem
                    key={String(savedLocation._id)}
                    location={savedLocation}
                    type="saved"
                    onClick={() => onSelectLocation(savedLocation)}
                    isRTL={isRTL}
                  />
                ))}
                {hasNextMyPlaces && (
                  <button
                    type="button"
                    onClick={onLoadMoreMyPlaces}
                    disabled={isFetchingNextMyPlaces}
                    className="w-full px-3 py-2 text-sm text-blue-600 hover:bg-gray-100 rounded-md border-t border-gray-200">
                    {isFetchingNextMyPlaces
                      ? t('location.loading', 'Loading...')
                      : t('location.loadMore', 'Load More')}
                  </button>
                )}
              </>
            ) : (
              <div className="px-3 py-4 text-center text-sm text-gray-500">
                {t('location.noSavedLocations', 'No saved locations')}
              </div>
            )}
          </div>
        )}

        {activeTab === 'client' && (
          <div className="pb-2">
            {isLoadingClientPlaces ? (
              <div className="px-3 py-4 text-center text-sm text-gray-500">
                {t('location.loading', 'Loading...')}
              </div>
            ) : clientPlaces.length > 0 ? (
              <>
                {clientPlaces.map(savedLocation => (
                  <LocationItem
                    key={String(savedLocation._id)}
                    location={savedLocation}
                    type="saved"
                    onClick={() => onSelectLocation(savedLocation)}
                    isRTL={isRTL}
                  />
                ))}
                {hasNextClientPlaces && (
                  <button
                    type="button"
                    onClick={onLoadMoreClientPlaces}
                    disabled={isFetchingNextClientPlaces}
                    className="w-full px-3 py-2 text-sm text-blue-600 hover:bg-gray-100 rounded-md border-t border-gray-200">
                    {isFetchingNextClientPlaces
                      ? t('location.loading', 'Loading...')
                      : t('location.loadMore', 'Load More')}
                  </button>
                )}
              </>
            ) : (
              <div className="px-3 py-4 text-center text-sm text-gray-500">
                {t('location.noSavedLocations', 'No saved locations')}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
